import { InputHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  helperText?: string;
  error?: string;
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      helperText,
      error,
      indeterminate = false,
      className,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || props.name || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    return (
      <div className="flex flex-col">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              ref={(node) => {
                if (node) {
                  node.indeterminate = indeterminate;
                }
                if (typeof ref === 'function') {
                  ref(node);
                } else if (ref) {
                  ref.current = node;
                }
              }}
              id={checkboxId}
              type="checkbox"
              disabled={disabled}
              className={clsx(
                // Base styles
                'h-4 w-4 rounded border',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                // States
                hasError
                  ? 'border-red-300 text-red-600 focus:ring-red-500'
                  : 'border-gray-300 text-primary-600 focus:ring-primary-500',
                // Custom className
                className
              )}
              {...props}
            />
          </div>

          {label && (
            <div className="ml-3 flex-1">
              <label
                htmlFor={checkboxId}
                className={clsx(
                  'text-sm font-medium',
                  disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'
                )}
              >
                {label}
              </label>
              {helperText && (
                <p className="text-sm text-gray-500 mt-0.5">{helperText}</p>
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="mt-1.5 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
